/**
 * Routes a QuizGameSpec to the right archetype engine and renders the
 * ResultScreen when the run finishes.
 */
import { useState } from "react";
import type { GameRunResult } from "@/types/gameSpec";
import type { QuizGameSpec } from "@/types/quizGameSpec";
import { ResultScreen } from "../dynamic/ResultScreen";
import QuizCatcher from "./QuizCatcher";
import WordSort from "./WordSort";
import MatchingPairs from "./MatchingPairs";
import TypeRunner from "./TypeRunner";
import { trackEvent } from "@/lib/analytics/ga";
import { useEffect } from "react";

interface Props {
  spec: QuizGameSpec;
  gameId: string;
  grade: string | number;
  onComplete: (r: GameRunResult) => void;
  onExit?: () => void;
  sharedFromUserId?: string | null;
}

export const ArchetypeRenderer = ({ spec, gameId, grade, onComplete, onExit, sharedFromUserId }: Props) => {
  const [result, setResult] = useState<GameRunResult | null>(null);
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    trackEvent("game_play_started", { game_id: gameId, grade: String(grade) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = (r: GameRunResult) => {
    trackEvent("game_play_completed", {
      game_id: gameId, score: r.score, won: r.won,
      time_spent: Math.round(r.time_spent_ms / 1000),
    });
    setResult(r);
    onComplete(r);
  };

  if (result) {
    return (
      <ResultScreen
        gameId={gameId}
        result={result}
        gameTitle={spec.meta.title}
        onPlayAgain={() => { setResult(null); setRunKey((k) => k + 1); }}
        onExit={onExit}
        sharedFromUserId={sharedFromUserId ?? null}
      />
    );
  }

  const Engine =
    spec.archetype === "word-sort" ? WordSort
    : spec.archetype === "matching-pairs" ? MatchingPairs
    : spec.archetype === "type-runner" ? TypeRunner
    : QuizCatcher;

  return <Engine key={runKey} spec={spec} onComplete={handleComplete} onExit={onExit} />;
};

export default ArchetypeRenderer;
